export const EMAIL_CAMPAIGN_COOKIE = "email_campaign";
export const DEFAULT_BOOKING_PARAM_NAME = "bookingAgentKey";

// Singenuity booking agent that attributes a sale to an email campaign. Every
// booking href on the site ships with the default web agent (2093) baked in;
// decorating an href overwrites that key rather than adding one, so a visitor
// is only ever counted under a single agent.
export const EMAIL_BOOKING_PARAM_VALUE = "2227";

// ChatGPT Ads attribution. OpenAI appends `oppref` to the ad's landing URL, but
// checkout happens on book.singenuity.com, and the OpenAI pixel there (loaded
// by the Cajun Encounters GTM container) can only credit the booking if it sees
// the token. No cookie can follow the visitor across that domain boundary, so
// proxy.ts keeps it in `nolwt_oppref` and booking links hand it to Singenuity
// in the URL. The full pipeline is documented in cajun-encounters-site's
// docs/openai-ads-conversions.md.
export const OPPREF_QUERY_PARAM = "oppref";
export const OPPREF_COOKIE = "nolwt_oppref";

// The token is opaque and OpenAI asks that it be passed through unmodified — so
// values that don't look like one are rejected outright rather than sanitized.
// This param reaches both a Set-Cookie header and an outbound URL, and neither
// is a place to be forgiving about crafted input.
const OPPREF_PATTERN = /^[\w.~-]{1,512}$/;

// Every booking URL on this site lives under Singenuity location 461. Scoping
// to it keeps the token off links to any other Singenuity client, such as the
// distillery (692).
const BOOKING_PATH_PREFIX = "/461/";

export function isValidOppref(
  value: string | null | undefined,
): value is string {
  return typeof value === "string" && OPPREF_PATTERN.test(value);
}

export function readCookie(cookieString: string, name: string) {
  const prefix = `${name}=`;
  const match = cookieString
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefix));

  if (!match) return null;

  const raw = match.slice(prefix.length);

  try {
    return decodeURIComponent(raw);
  } catch {
    // decodeURIComponent throws on a lone "%" or a bad escape. This runs inside
    // a click handler on every navigation, so a malformed cookie must not be
    // able to throw there.
    return raw;
  }
}

export function hasEmailCampaignCookie(cookieString: string) {
  return cookieString
    .split(";")
    .some((cookie) => cookie.trim().startsWith(`${EMAIL_CAMPAIGN_COOKIE}=`));
}

export function getCampaignBookingHref({
  href,
  hasCampaign,
  paramName = DEFAULT_BOOKING_PARAM_NAME,
  paramValue = EMAIL_BOOKING_PARAM_VALUE,
}: {
  href: string;
  hasCampaign: boolean;
  paramName?: string;
  paramValue?: string;
}) {
  if (!hasCampaign) return href;

  try {
    const url = new URL(href);

    if (!url.hostname.includes("singenuity")) return href;

    // Idempotent: re-decorating an already-decorated link is a no-op, which
    // matters because the click handler can fire twice on the same anchor.
    if (url.searchParams.get(paramName) === paramValue) return href;

    url.searchParams.set(paramName, paramValue);
    return url.toString();
  } catch {
    // Relative hrefs and malformed URLs both land here; neither is a booking
    // link, so the original value is the correct answer.
    return href;
  }
}

export function appendOpprefToBookingHref({
  href,
  oppref,
}: {
  href: string;
  oppref: string | null | undefined;
}) {
  if (!isValidOppref(oppref)) return href;

  try {
    const url = new URL(href);

    if (
      !url.hostname.includes("singenuity") ||
      !url.pathname.startsWith(BOOKING_PATH_PREFIX)
    ) {
      return href;
    }

    // Idempotent for the same reason as getCampaignBookingHref.
    if (url.searchParams.get(OPPREF_QUERY_PARAM) === oppref) return href;

    url.searchParams.set(OPPREF_QUERY_PARAM, oppref);
    return url.toString();
  } catch {
    return href;
  }
}
